/**
 * 详情页组件（统一处理新闻、通知、政策详情）
 * 数据从API获取
 */

import React, { useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import ContentRenderer from '../../../../components/common/ContentRenderer';
import { NewsItem as News } from '../../../../types';
import { PolicyItem, policyApi } from '../../../../services/policyService';
import { newsService } from '../../../../services/newsService';
import './DetailPage.css';

// 扩展News类型以包含详情页需要的字段
interface ExtendedNews extends News {
    publishTime?: string;
    coverImage?: string;
    summary?: string;
    author?: string;
    tags?: string | string[];
    attachmentUrls?: string | string[];
}

interface DetailPageProps {
    news?: News[];
    policies?: PolicyItem[];
    loading?: boolean;
    type: 'news' | 'notice' | 'policy';
}

const DetailPage: React.FC<DetailPageProps> = ({ news = [], policies = [], loading: propsLoading = false, type }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [detailData, setDetailData] = useState<News | PolicyItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 从API加载详情数据
    useEffect(() => {
        const loadDetailData = async () => {
            // 从URL路径中提取ID
            let id: number = 0;
            const pathname = location.pathname;
            
            // 匹配 /show/news/460 或 /show/policy/123 等格式
            // 同时支持 /news/460 格式（如果路由中没有/show前缀）
            const match = pathname.match(/\/(show\/)?(news|notice|policy)\/(\d+)$/);
            if (match && match[3]) {
                id = parseInt(match[3]);
            }
            
            if (!id || isNaN(id)) {
                setError('无效的ID');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            setDetailData(null); // 重置数据

            try {
                if (type === 'policy') {
                    // 优先从API获取详情数据（确保数据最新）
                    // props中的数据作为备用，如果API失败再尝试使用
                    try {
                        const response = await policyApi.getById(id);
                        
                        if (!response) {
                            setError('政策不存在');
                            setLoading(false);
                            return;
                        }
                        
                        // 检查响应格式 - httpClient统一返回ApiResponse格式
                        if (response.code === 200 || response.code === 0) {
                            // 标准响应格式: { code: 200, data: {...} }
                            const data = response.data;
                            if (data && (data.id || data.title || data.content || data.fileName)) {
                                setDetailData(data as PolicyItem);
                                setLoading(false);
                            } else {
                                // API返回数据为空，尝试从props中查找
                                const found = policies.find(item => item.id === id);
                                if (found) {
                                    setDetailData(found);
                                    setLoading(false);
                                } else {
                                    setError('政策不存在');
                                    setLoading(false);
                                }
                            }
                        } else if (response.code && response.code !== 200) {
                            // API返回错误
                            // API返回错误，尝试从props中查找
                            const found = policies.find(item => item.id === id);
                            if (found) {
                                setDetailData(found);
                                setLoading(false);
                            } else {
                                setError(response.message || '政策不存在');
                                setLoading(false);
                            }
                        } else {
                            // 未识别的响应格式，尝试从props中查找
                            const found = policies.find(item => item.id === id);
                            if (found) {
                                setDetailData(found);
                                setLoading(false);
                            } else {
                                setError('政策不存在');
                                setLoading(false);
                            }
                        }
                    } catch (apiError: any) {
                        // API调用失败，尝试从props中查找
                        const found = policies.find(item => item.id === id);
                        if (found) {
                            setDetailData(found);
                            setLoading(false);
                        } else {
                            if (apiError.response) {
                                if (apiError.response.status === 404) {
                                    setError('政策不存在');
                                } else {
                                    setError(`加载失败: ${apiError.response.data?.message || apiError.message}`);
                                }
                            } else {
                                setError(`加载失败: ${apiError.message || '网络错误'}`);
                            }
                            setLoading(false);
                        }
                    }
                } else {
                    // 优先从API获取详情数据（确保数据最新）
                    // props中的数据作为备用，如果API失败再尝试使用
                    try {
                        const response = await newsService.getById(id);
                        
                        if (!response) {
                            setError('新闻不存在');
                            setLoading(false);
                            return;
                        }
                        
                        // 检查响应格式 - httpClient统一返回ApiResponse格式
                        if (response.code === 200 || response.code === 0) {
                            // 标准响应格式: { code: 200, data: {...} }
                            const data = response.data;
                            if (data && (data.id || data.title || data.content)) {
                                setDetailData(data as News);
                                setLoading(false);
                            } else {
                                // API返回数据为空，尝试从props中查找
                                const found = news.find(item => item.id === id);
                                if (found) {
                                    setDetailData(found);
                                    setLoading(false);
                                } else {
                                    setError('新闻不存在');
                                    setLoading(false);
                                }
                            }
                        } else if (response.code && response.code !== 200) {
                            // API返回错误
                            // API返回错误，尝试从props中查找
                            const found = news.find(item => item.id === id);
                            if (found) {
                                setDetailData(found);
                                setLoading(false);
                            } else {
                                setError(response.message || '新闻不存在');
                                setLoading(false);
                            }
                        } else {
                            // 未识别的响应格式，尝试从props中查找
                            const found = news.find(item => item.id === id);
                            if (found) {
                                setDetailData(found);
                                setLoading(false);
                            } else {
                                setError('新闻不存在');
                                setLoading(false);
                            }
                        }
                    } catch (apiError: any) {
                        // API调用失败，尝试从props中查找
                        const found = news.find(item => item.id === id);
                        if (found) {
                            setDetailData(found);
                            setLoading(false);
                        } else {
                            if (apiError.response) {
                                if (apiError.response.status === 404) {
                                    setError('新闻不存在');
                                } else {
                                    setError(`加载失败: ${apiError.response.data?.message || apiError.message}`);
                                }
                            } else {
                                setError(`加载失败: ${apiError.message || '网络错误'}`);
                            }
                            setLoading(false);
                        }
                    }
                }
            } catch (error) {
                setError('加载详情失败');
            } finally {
                setLoading(false);
            }
        };

        loadDetailData();
    }, [location.pathname, type]); // 依赖location.pathname而不是params.id，确保每次路径变化都会调用API

    // 加载中状态
    if (loading || propsLoading) {
        return (
            <div className="detail-page">
                <div className="detail-content">
                    <Spin size="large" />
                    <p style={{ marginTop: '16px' }}>加载中...</p>
                </div>
            </div>
        );
    }

    // 错误或数据不存在
    if (error || !detailData) {
        const backPath = type === 'policy' ? '/show/policy' : type === 'notice' ? '/show/news' : '/show/news';
        const title = type === 'policy' ? '政策法规' : type === 'notice' ? '通知公告' : '新闻';
        
        return (
            <div className="detail-page">
                <div className="detail-content">
                    <Button onClick={() => navigate(backPath)} className="back-button">
                        ← 返回
                    </Button>
                    <h1>{title}不存在</h1>
                    <p>抱歉，您访问的{title}不存在。</p>
                    <Button onClick={() => navigate(backPath)}>返回</Button>
                </div>
            </div>
        );
    }

    const isNews = type === 'news' || type === 'notice';
    const backPath = type === 'policy' ? '/show/policy' : '/show/news';

    // 处理标签
    const tags: string[] = (() => {
        if (!isNews || !(detailData as ExtendedNews).tags) {
            return [];
        }
        const tagsValue = (detailData as ExtendedNews).tags!;
        if (typeof tagsValue === 'string') {
            return tagsValue.split(',');
        }
        if (Array.isArray(tagsValue)) {
            return tagsValue;
        }
        return [];
    })();

    // 处理附件
    const attachments: string[] = (() => {
        if (!isNews || !(detailData as ExtendedNews).attachmentUrls) {
            return [];
        }
        const urlsValue = (detailData as ExtendedNews).attachmentUrls!;
        if (typeof urlsValue === 'string') {
            try {
                const parsed = JSON.parse(urlsValue);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        if (Array.isArray(urlsValue)) {
            return urlsValue;
        }
        return [];
    })();

    // 处理图片路径
    const getImageUrl = (imgPath?: string) => {
        if (!imgPath) return '';
        if (imgPath.startsWith('data:') || imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
            return imgPath;
        }
        return imgPath.startsWith('/') ? imgPath : '/' + imgPath;
    };

    return (
        <div className="detail-page">
            <div className="detail-content">
                <Button onClick={() => navigate(backPath)} className="back-button">
                    ← 返回
                </Button>

                {/* 标题 */}
                <h1 className="detail-title">
                    {isNews ? detailData.title : (detailData as PolicyItem).fileName}
                </h1>

                {/* 元信息 */}
                <div className="detail-meta">
                    {tags.length > 0 && (
                        <div className="detail-tags">
                            {tags.map((tag: string, index: number) => (
                                <span key={index} className="tag-item">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="detail-info">
                        {isNews && (
                            <>
                                <span className="detail-date">
                                    发布日期: {(detailData as ExtendedNews).publishTime?.substring(0, 10) || (detailData as News).date || ''}
                                </span>
                                {(detailData as ExtendedNews).author && (
                                    <span className="detail-source">
                                        来源: {(detailData as ExtendedNews).author}
                                    </span>
                                )}
                            </>
                        )}
                        {!isNews && (
                            <>
                                <span className="detail-date">
                                    {(detailData as PolicyItem).issuanceDate || '暂无日期'}
                                </span>
                                {(detailData as PolicyItem).webLink && (
                                    <a
                                        href={(detailData as PolicyItem).webLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="external-link"
                                    >
                                        查看原文 →
                                    </a>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* 分割线 */}
                <div className="detail-divider"></div>

                {/* 封面图片 */}
                {isNews && (detailData as ExtendedNews).coverImage && (
                    <div className="detail-cover">
                        <img
                            src={getImageUrl((detailData as ExtendedNews).coverImage)}
                            alt="封面图片"
                            className="cover-image"
                        />
                    </div>
                )}

                {/* 摘要 - 只有当摘要存在且不为空时才显示 */}
                {isNews && (detailData as ExtendedNews).summary && (detailData as ExtendedNews).summary?.trim() !== '' && (
                    <div className="detail-summary">
                        <h3 className="summary-title">摘要</h3>
                        <p className="summary-content">{(detailData as ExtendedNews).summary}</p>
                    </div>
                )}

                {/* 内容 */}
                <div className="detail-body">
                    {isNews ? (
                        <ContentRenderer content={(detailData as News).content || (detailData as ExtendedNews).summary || ''} />
                    ) : (
                        <ContentRenderer content={(detailData as PolicyItem).content || ''} />
                    )}
                </div>

                {/* 附件 */}
                {isNews && attachments.length > 0 && (
                    <div className="detail-attachments">
                        <div className="attachments-header">
                            <div className="header-indicator"></div>
                            <h3 className="attachments-title">附件下载</h3>
                        </div>
                        <div className="attachments-list">
                            {attachments.map((url: string, index: number) => {
                                const fileName = url.split('/').pop() || `附件${index + 1}`;
                                return (
                                    <div key={index} className="attachment-item">
                                        <span className="attachment-bullet">•</span>
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="attachment-link"
                                        >
                                            {fileName}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DetailPage;

